import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import SAT_HDS from "../config/satHds";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * SURVIVAL OF THE FITTEST™ — public landing page.
 * Grundfrage: Wie handlungs- und entwicklungsfähig bist du, wenn das Leben Veränderung verlangt?
 * Keine Fitness-Challenge, keine Darwinistische Wertung, kein Wettbewerb gegen andere.
 */

const RadarPreview = () => {
  const dims = SAT_HDS.experiences.survival.dimensions;
  const n = dims.length;
  const cx = 240;
  const cy = 240;
  const rOuter = 200;
  const sample = [72, 63, 84, 58, 76, 55, 82, 78, 68];

  return (
    <svg viewBox="0 0 480 480" className="w-full max-w-lg mx-auto" aria-hidden="true">
      <defs>
        <radialGradient id="rGlow" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="#7C6FE0" stopOpacity="0.22" />
          <stop offset="100%" stopColor="#7C6FE0" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx={cx} cy={cy} r={rOuter + 10} fill="url(#rGlow)" />
      {[0.25, 0.5, 0.75, 1].map((s) => (
        <circle key={s} cx={cx} cy={cy} r={rOuter * s} fill="none" stroke="#D4AF37" strokeWidth="0.6" opacity="0.28" />
      ))}
      {dims.map((d, i) => {
        const a = (i / n) * Math.PI * 2 - Math.PI / 2;
        const lx = cx + Math.cos(a) * (rOuter + 26);
        const ly = cy + Math.sin(a) * (rOuter + 26);
        const anchor = Math.cos(a) > 0.15 ? "start" : Math.cos(a) < -0.15 ? "end" : "middle";
        return (
          <g key={d.key}>
            <line x1={cx} y1={cy} x2={cx + Math.cos(a) * rOuter} y2={cy + Math.sin(a) * rOuter}
                  stroke="#D4AF37" strokeWidth="0.4" opacity="0.35" />
            <text x={lx} y={ly} textAnchor={anchor} dominantBaseline="middle" fontSize="10.5" fill="#F6E7B8" opacity="0.85" fontFamily="serif">
              {d.label}
            </text>
          </g>
        );
      })}
      <polygon
        points={sample
          .map((v, i) => {
            const a = (i / n) * Math.PI * 2 - Math.PI / 2;
            const r = (v / 100) * rOuter;
            return `${(cx + Math.cos(a) * r).toFixed(1)},${(cy + Math.sin(a) * r).toFixed(1)}`;
          })
          .join(" ")}
        fill="#D4AF37"
        fillOpacity="0.22"
        stroke="#D4AF37"
        strokeWidth="1.4"
      />
      {sample.map((v, i) => {
        const a = (i / n) * Math.PI * 2 - Math.PI / 2;
        const r = (v / 100) * rOuter;
        return <circle key={i} cx={cx + Math.cos(a) * r} cy={cy + Math.sin(a) * r} r="2.8" fill="#F6E7B8" />;
      })}
      <text x={cx} y={cy - 4} textAnchor="middle" fontSize="9" fill="#D4AF37" opacity="0.7" letterSpacing="4">BEISPIEL</text>
    </svg>
  );
};

const SurvivalOfTheFittestLanding = () => {
  const cfg = SAT_HDS.experiences.survival;
  const [entitlement, setEntitlement] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${API}/api/self-discovery/me/entitlements`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const item = (res.data.items || []).find((e) => e.assessment_slug === cfg.key);
        setEntitlement(item || null);
      } catch (_) {
        setEntitlement(null);
      }
    })();
  }, [cfg.key]);

  const isActive = entitlement?.status === "active";
  const isRequested = entitlement?.status === "requested";

  const levels = [
    ["01", "The Survivor", "Reaktion und Stabilisierung stehen im Vordergrund."],
    ["02", "The Adapter", "Anpassung und Lernen werden stärker."],
    ["03", "The Transformer", "Veränderung wird aktiv für Entwicklung genutzt."],
    ["04", "The Life Master", "Selbstführung, Integration und Gestaltung rücken ins Zentrum."],
  ];

  return (
    <div data-testid="survival-landing" className="bg-[#050914] text-white min-h-screen">
      <SEO
        title="Survival of the Fittest™ · Die Architektur deiner Lebensfähigkeit"
        description="Survival of the Fittest ist eine exklusive SAT Experience. Erkenne, welche inneren Ressourcen dich tragen und welche Fähigkeiten dein nächstes Lebenskapitel verlangt."
        path={SAT_HDS.paths.survival}
      />

      {/* HERO */}
      <section className="relative overflow-hidden pt-24 sm:pt-32 pb-20">
        <div className="pointer-events-none absolute inset-0"
             style={{ background: "radial-gradient(1000px 500px at 50% 0%, rgba(124,111,224,0.20), transparent 70%), radial-gradient(600px 400px at 20% 100%, rgba(212,175,55,0.10), transparent 70%)" }} />
        <div className="relative mx-auto max-w-5xl px-6 text-center">
          <div className="text-[11px] tracking-[0.4em] text-[#D4AF37] uppercase mb-6">SAT SELF DISCOVERY · 02</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-[#F6E7B8]">
            {cfg.trademark}
          </h1>
          <p className="mt-6 text-xl sm:text-2xl italic text-white/70">{cfg.hero}</p>
          <p className="mt-10 text-base sm:text-lg text-white/75 max-w-2xl mx-auto leading-relaxed">
            {cfg.intro}
          </p>
          <div className="mt-10 text-xs tracking-[0.4em] uppercase text-[#D4AF37]">
            {cfg.arc}
          </div>

          <div className="mt-12">
            {isActive ? (
              <button
                data-testid="cta-open-experience"
                onClick={() => alert("Assessment startet in Milestone 3")}
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#D4AF37] text-[#050914] font-medium tracking-wider hover:bg-[#F6E7B8] transition"
              >
                {cfg.ctaSecondary}
              </button>
            ) : isRequested ? (
              <div className="inline-block px-6 py-3 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] tracking-widest text-sm">
                ZUGANG ANGEFRAGT · SAT PRÜFT
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/erstgespraech"
                  data-testid="cta-request-access"
                  className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#D4AF37] text-[#050914] font-medium tracking-wider hover:bg-[#F6E7B8] transition"
                >
                  ZUGANG ANFRAGEN
                </Link>
                <Link
                  to="/ecosystem"
                  className="inline-flex items-center gap-3 px-6 py-3 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] tracking-widest text-sm hover:border-[#D4AF37] transition"
                >
                  SAT PROGRAMME ENTDECKEN
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* PREVIEW RADAR */}
      <section className="py-20 border-t border-white/5">
        <div className="mx-auto max-w-5xl px-6 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="text-[10px] tracking-[0.3em] text-[#D4AF37] uppercase mb-4">NEUN DIMENSIONEN</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#F6E7B8] leading-tight">
              Deine Lebensfähigkeit — als Struktur sichtbar gemacht.
            </h2>
            <div className="mt-8 grid grid-cols-2 gap-2 max-w-md">
              {cfg.dimensions.map((d) => (
                <div key={d.key} className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-sm text-white/80">
                  {d.label}
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-[#D4AF37]/15 bg-white/[0.02] p-6 sm:p-10">
            <RadarPreview />
          </div>
        </div>
      </section>

      {/* LEVELS */}
      <section className="py-20 bg-[#080D1E] border-t border-white/5">
        <div className="mx-auto max-w-5xl px-6">
          <div className="text-[10px] tracking-[0.3em] text-[#D4AF37] uppercase mb-4">ENTWICKLUNGSSTUFEN</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#F6E7B8] leading-tight max-w-2xl">
            Vier Ebenen deiner Entwicklungsreise.
          </h2>
          <p className="mt-4 text-white/60 text-sm max-w-2xl">
            Keine Stufe ist besser als eine andere — sie beschreibt, wo du gerade stehst und in welche Richtung dein nächster Schritt gehen kann.
          </p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {levels.map(([num, title, desc]) => (
              <div key={num} className="rounded-xl border border-[#D4AF37]/15 bg-white/[0.02] p-6 hover:border-[#D4AF37]/50 transition">
                <div className="text-[10px] tracking-[0.3em] text-[#D4AF37] uppercase mb-3">Level {num}</div>
                <div className="font-serif text-xl text-[#F6E7B8] mb-3">{title}</div>
                <p className="text-sm text-white/65 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-24 border-t border-white/5">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="font-serif text-2xl sm:text-3xl text-[#F6E7B8] italic leading-snug">
            „{cfg.centralMessage}“
          </p>
          <div className="mt-10">
            {!isActive && !isRequested && (
              <Link
                to="/erstgespraech"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#D4AF37] text-[#050914] font-medium tracking-wider hover:bg-[#F6E7B8] transition"
              >
                {cfg.cta}
              </Link>
            )}
          </div>
          <p className="mt-8 text-xs text-white/40 tracking-widest uppercase">
            Keine Fitness-Challenge · keine Wertung · nur Klarheit.
          </p>
        </div>
      </section>
    </div>
  );
};

export default SurvivalOfTheFittestLanding;
